import {
  Sparkles,
  MapPin,
  Users,
  CalendarDays,
  Gift,
  Check,
  Navigation,
  Heart,
  Globe,
  Tag,
  Ribbon,
} from "lucide-react";
import { DialogTitle } from "../../components/ui/dialog";
import { EmailWithCopy } from "./ContactFormDialog";

export const TYPE_META = {
  hebergement: { label: "Hébergement", color: "#8FA07C" },
  "bien-etre": { label: "Bien-être", color: "#C6870F" },
  "les-deux": { label: "Hébergement & Bien-être", color: "#B5762A" },
};

export const lieuShareHref = (lieu) =>
  `https://wa.me/?text=${encodeURIComponent(
    `Découvrez « ${lieu.name} » à ${lieu.commune} — un lieu T'POZ près de chez vous : ${window.location.origin}/lieu/${lieu.id}`
  )}`;

const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-pblv-sage/15 px-3 py-1.5 text-xs font-semibold text-[#5f6b4f]">
    <Check className="h-3 w-3" strokeWidth={2.5} /> {children}
  </span>
);

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.15em] text-pblv-inksoft">
      <Icon className="h-4 w-4 text-pblv-mauve" strokeWidth={1.8} /> {title}
    </p>
    {children}
  </div>
);

export const FicheContent = ({ lieu, inModal = false }) => {
  const meta = TYPE_META[lieu.type] || TYPE_META["bien-etre"];
  const itinerary = lieu.maps_url || `https://www.google.com/maps/dir/?api=1&destination=${lieu.lat},${lieu.lng}`;

  const title = (
    <div className="mt-3">
      <h3 className="font-serif text-3xl font-medium text-pblv-ink md:text-4xl">
        {lieu.experience_titre || lieu.name}
      </h3>
      <p className="mt-2 text-base font-semibold text-pblv-mauve">{lieu.name}</p>
    </div>
  );

  return (
    <>
      {/* Gallery */}
      {lieu.photos?.length > 0 && (
        <div className="grid grid-cols-3 gap-1">
          {lieu.photos.slice(0, 3).map((src, i) => (
            <img
              key={i}
              src={src}
              alt={`${lieu.name} ${i + 1}`}
              className={i === 0 ? "col-span-3 aspect-[4/3] w-full object-cover" : "h-36 w-full object-cover md:h-44"}
              loading="lazy"
            />
          ))}
        </div>
      )}

      <div className="p-6 md:p-8">
        <span
          className="inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-white"
          style={{ background: meta.color }}
        >
          {meta.label}
        </span>
        {lieu.octobre_rose && (
          <span
            data-testid="fiche-ruban-rose"
            className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#FCE4EC] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#C2185B]"
          >
            <Ribbon className="h-3.5 w-3.5" strokeWidth={2} /> Octobre Rose
          </span>
        )}
        {inModal ? <DialogTitle asChild>{title}</DialogTitle> : title}
        <p className="mt-1 flex items-center gap-1.5 text-sm font-semibold text-pblv-mauve">
          <MapPin className="h-4 w-4" strokeWidth={1.8} />
          {lieu.commune}
          {lieu.secteur ? ` · ${lieu.secteur}` : ""}, Guadeloupe
        </p>
        {lieu.description && (
          <p className="mt-4 leading-relaxed text-pblv-inksoft">{lieu.description}</p>
        )}

        {lieu.esprit?.length > 0 && (
          <div className="mt-6 rounded-2xl border border-pblv-mauve/20 bg-pblv-mauve/5 p-5">
            <p className="mb-3 flex items-center gap-2 font-serif text-lg font-medium text-pblv-ink">
              <Heart className="h-4 w-4 text-pblv-mauve" strokeWidth={1.8} /> L'esprit de cette T'POZ
            </p>
            <div className="flex flex-wrap gap-2">
              {lieu.esprit.map((e) => (
                <span key={e} className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-pblv-mauvedark">
                  {e}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {lieu.experiences?.length > 0 && (
            <Section icon={Sparkles} title="Expériences proposées">
              <div className="flex flex-wrap gap-2">
                {lieu.experiences.map((e) => <Chip key={e}>{e}</Chip>)}
              </div>
            </Section>
          )}
          {lieu.comprend?.length > 0 && (
            <Section icon={Gift} title="Ce que comprend cette T'POZ">
              <ul className="flex flex-col gap-1.5 text-sm text-pblv-ink">
                {lieu.comprend.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-pblv-sage" strokeWidth={2.5} /> {c}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {lieu.capacite && (
            <Section icon={Users} title="Pour combien de personnes ?">
              <p className="text-sm text-pblv-ink">{lieu.capacite}</p>
            </Section>
          )}
          {lieu.periode && (
            <Section icon={CalendarDays} title="Période de validité">
              <p className="text-sm text-pblv-ink">{lieu.periode}</p>
            </Section>
          )}
          {lieu.conditions?.length > 0 && (
            <Section icon={CalendarDays} title="Conditions de réservation">
              <ul className="flex flex-col gap-1 text-sm text-pblv-ink">
                {lieu.conditions.map((c) => <li key={c}>· {c}</li>)}
              </ul>
            </Section>
          )}
        </div>

        {(lieu.tarif || lieu.contribution?.length > 0) && (
          <div className="mt-6 rounded-2xl border border-pblv-ink/10 bg-white p-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {lieu.tarif && (
                <Section icon={Tag} title="Tarif public (hors remise)">
                  <p className="text-sm text-pblv-ink" data-testid={`fiche-tarif-${lieu.id}`}>{lieu.tarif}</p>
                </Section>
              )}
              {lieu.contribution?.length > 0 && (
                <Section icon={Gift} title="Avantage T'POZ">
                  <ul className="flex flex-col gap-1.5 text-sm text-pblv-ink">
                    {lieu.contribution.map((c) => (
                      <li key={c} className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-pblv-mauve" /> {c}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </div>
            <p className="mt-4 border-t border-pblv-ink/10 pt-3 text-center text-xs italic text-pblv-inksoft" data-testid={`fiche-tarif-mention-${lieu.id}`}>
              Tarifs et disponibilités : rapprochez-vous {lieu.type === "bien-etre" ? "du partenaire" : "de l'hébergeur"}.
            </p>
          </div>
        )}

        {lieu.petits_plus?.length > 0 && (
          <div className="mt-6">
            <Section icon={Sparkles} title="Les petits plus">
              <div className="flex flex-wrap gap-2">
                {lieu.petits_plus.map((p) => (
                  <span key={p} className="rounded-full border border-pblv-ink/10 bg-white px-3 py-1.5 text-xs font-medium text-pblv-inksoft">
                    {p}
                  </span>
                ))}
              </div>
            </Section>
          </div>
        )}

        {lieu.unique && (
          <p className="mt-6 rounded-2xl bg-pblv-bg2 p-5 font-serif text-lg italic leading-snug text-pblv-ink">
            « {lieu.unique} »
          </p>
        )}

        <div className="mt-7 flex flex-wrap gap-3">
          <a
            href={itinerary}
            target="_blank"
            rel="noreferrer"
            data-testid={`fiche-itineraire-${lieu.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-pblv-ink/15 px-6 py-3.5 text-sm font-semibold text-pblv-ink transition-all hover:-translate-y-0.5 hover:border-pblv-ink/40"
          >
            <Navigation className="h-4 w-4" strokeWidth={1.8} /> Voir l'itinéraire
          </a>
          {lieu.site_url && (
            <a
              href={lieu.site_url}
              target="_blank"
              rel="noreferrer"
              data-testid={`fiche-site-${lieu.id}`}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-pblv-mauve/40 px-6 py-3.5 text-sm font-semibold text-pblv-mauve transition-all hover:-translate-y-0.5 hover:border-pblv-mauve hover:bg-pblv-mauve/5"
            >
              <Globe className="h-4 w-4" strokeWidth={1.8} /> Site du partenaire
            </a>
          )}
        </div>

        {(lieu.tel || lieu.email) && (
          <p className="mt-4 text-center text-sm text-pblv-inksoft" data-testid={`fiche-contact-${lieu.id}`}>
            Contact réservation :{" "}
            {lieu.tel && (
              <a href={`tel:${lieu.tel.replace(/\s/g, "")}`} className="font-semibold text-pblv-mauve hover:underline">
                {lieu.tel}
              </a>
            )}
            {lieu.tel && lieu.email && " · "}
            {lieu.email && (
              <EmailWithCopy email={lieu.email} testId={`fiche-email-${lieu.id}`} className="font-semibold text-pblv-mauve" />
            )}
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a
            href="/demande"
            data-testid={`fiche-demander-${lieu.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-pblv-mauve px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:bg-pblv-mauvedark"
          >
            Demander cette T'POZ
          </a>
          <a
            href="/offrir"
            data-testid={`fiche-offrir-${lieu.id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-pblv-mauve/40 px-6 py-3.5 text-sm font-semibold text-pblv-mauve transition-all hover:-translate-y-0.5 hover:border-pblv-mauve hover:bg-pblv-mauve/5"
          >
            Offrir cette T'POZ
          </a>
        </div>
        {lieu.geo_note && (
          <p className="mt-2 text-center text-xs italic text-pblv-inksoft" data-testid={`fiche-geonote-${lieu.id}`}>
            {lieu.geo_note}
          </p>
        )}
      </div>
    </>
  );
};
